/**
 * Builds structured search filter trees for the Astera oncology external table.
 */

/** Single predicate condition node for BigQuery structured filters. */
export interface FilterCondition {
  type: "condition";
  field: string;
  operator: string;
  value?: string | boolean | Array<string | boolean>;
}

/** AND/OR group node for BigQuery structured filters. */
export interface FilterGroup {
  type: "group";
  operator: "and" | "or";
  children: (FilterCondition | FilterGroup)[];
}

/**
 * Builds the OR search filter tree matching seq, patient_name, or first/last name.
 *
 * @param searchTerm - Raw search text from the table search input
 * @returns FilterGroup: OR group with seq, patient_name, and name AND conditions
 */
export const buildSearchFilterTree = (searchTerm: string): FilterGroup => {
  const trimmed = searchTerm.trim();
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? trimmed;
  const lastName = parts.slice(1).join(" ") || firstName;

  return {
    type: "group",
    operator: "or",
    children: [
      {
        type: "condition",
        field: "seq",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "condition",
        field: "patient_name",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "group",
        operator: "and",
        children: [
          {
            type: "condition",
            field: "patient_first_name",
            operator: "contains",
            value: firstName,
          },
          {
            type: "condition",
            field: "patient_last_name",
            operator: "contains",
            value: lastName,
          },
        ],
      },
    ],
  };
};

/**
 * Converts legacy flat sidebar filters into predicate condition nodes.
 *
 * @param apiFilters - Flat filter map from processFilterData
 * @returns FilterCondition[]: Predicate conditions for sidebar filters
 */
export const convertLegacyFiltersToConditions = (
  apiFilters: Record<string, string[] | null>,
): FilterCondition[] => {
  const conditions: FilterCondition[] = [];

  Object.entries(apiFilters).forEach(([field, values]) => {
    if (!values || values.length === 0) {
      return;
    }

    if (field === "dumped_at" && values.length >= 2) {
      conditions.push({
        type: "condition",
        field: "dumped_at",
        operator: ">=",
        value: values[0],
      });
      conditions.push({
        type: "condition",
        field: "dumped_at",
        operator: "<=",
        value: values[1],
      });
      return;
    }

    conditions.push({
      type: "condition",
      field,
      operator: "is_any_of",
      value: values,
    });
  });

  return conditions;
};

/**
 * Merges the search filter tree with any active sidebar filters.
 *
 * @param searchTerm - Raw search text from the table search input
 * @param apiFilters - Flat filter map from processFilterData (may include sidebar filters)
 * @returns FilterGroup | Record<string, string[] | null>: Combined or search-only filter tree
 */
export const mergeSearchFiltersWithSidebarFilters = (
  searchTerm: string,
  apiFilters: Record<string, string[] | null>,
): FilterGroup | Record<string, string[] | null> => {
  const searchTree = buildSearchFilterTree(searchTerm);
  const sidebarFilters = { ...apiFilters };
  delete sidebarFilters.seq;

  const sidebarConditions = convertLegacyFiltersToConditions(sidebarFilters);

  if (sidebarConditions.length === 0) {
    return searchTree;
  }

  return {
    type: "group",
    operator: "and",
    children: [...sidebarConditions, searchTree],
  };
};
